import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";
import NumberLineInterval from "../../../../components/visuals/NumberLineInterval";

const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));
const pick = (values) => values[Math.floor(Math.random() * values.length)];
const SIGN_LATEX = { lt: "<", le: "\\le", gt: ">", ge: "\\ge" };

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

const lcm = (a, b) => Math.abs(a * b) / gcd(a, b);
const coeffX = (value) => value === 1 ? "x" : value === -1 ? "-x" : `${value}x`;
const linearLatex = (a, b) => b === 0
  ? coeffX(a)
  : `${coeffX(a)} ${b > 0 ? "+" : "-"} ${Math.abs(b)}`;
const flipKind = (kind) => ({ lt: "gt", le: "ge", gt: "lt", ge: "le" })[kind];

function intervalLatex(boundary, finalLess, inclusive) {
  if (finalLess) return `(-\\infty,\\;${boundary}${inclusive ? "]" : ")"}`;
  return `${inclusive ? "[" : "("}${boundary},\\;+\\infty)`;
}

export function generateTask(generationOptions = {}) {
  while (true) {
    const [leftDenominator, rightDenominator] = pick([[2, 3], [3, 4], [4, 6], [3, 5]]);
    const commonMultiple = lcm(leftDenominator, rightDenominator);
    const leftMultiplier = commonMultiple / leftDenominator;
    const rightMultiplier = commonMultiple / rightDenominator;
    const boundary = randInt(-6, 6);
    const leftCoefficient = pick([-4, -3, -2, 2, 3, 4]);
    const rightCoefficient = pick([2, 3, 4, 5, 6]);
    const leftConstant = pick([-6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6]);
    const scaledRightConstant =
      leftMultiplier * (leftCoefficient * boundary + leftConstant)
      - rightMultiplier * rightCoefficient * boundary;
    if (scaledRightConstant % rightMultiplier !== 0) continue;

    const rightConstant = scaledRightConstant / rightMultiplier;
    const reducedCoefficient =
      leftMultiplier * leftCoefficient - rightMultiplier * rightCoefficient;
    const reducedConstant =
      rightMultiplier * rightConstant - leftMultiplier * leftConstant;
    if (reducedCoefficient >= 0 || rightConstant === 0 || Math.abs(rightConstant) > 16) continue;

    const kind = generationOptions.kind || pick(["lt", "le", "gt", "ge"]);
    const finalKind = flipKind(kind);
    const finalLess = finalKind === "lt" || finalKind === "le";
    const inclusive = kind === "le" || kind === "ge";

    return {
      type: "LinearInequalityFractionsTask",
      data: {
        leftDenominator, rightDenominator, commonMultiple,
        leftMultiplier, rightMultiplier, leftCoefficient, rightCoefficient,
        leftConstant, rightConstant, reducedCoefficient, reducedConstant,
        boundary, kind, finalKind, finalLess, inclusive,
        latex: `\\dfrac{${linearLatex(leftCoefficient, leftConstant)}}{${leftDenominator}} ${SIGN_LATEX[kind]} \\dfrac{${linearLatex(rightCoefficient, rightConstant)}}{${rightDenominator}}`,
      },
    };
  }
}

export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż nierówność: <BlockMath math={data.latex} />
    </p>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <InlineMath math="x\\in" />
      <IntervalInput
        value={userInput?.S || ""}
        onChange={(value) => onChange({ ...userInput, S: value })}
        disabled={disabled}
        asString
        previewWithX
        placeholder="np. (-∞, 2] lub (2, ∞)"
      />
    </div>
  );
}

export function checkAnswer({ data }, userInput = {}) {
  const raw = typeof userInput?.S === "string" ? userInput.S : userInput?.S?.raw || "";
  const parsed = parseIntervalInput(raw);
  if (!parsed || parsed.kind !== "intervals" || parsed.intervals.length !== 1) return { correct: false };
  const interval = parsed.intervals[0];
  const sameBoundary = (value) => Math.abs(value - data.boundary) <= 1e-6;
  const correct = data.finalLess
    ? interval.left === -Infinity && sameBoundary(interval.right) && !!interval.rightClosed === !!data.inclusive
    : interval.right === Infinity && sameBoundary(interval.left) && !!interval.leftClosed === !!data.inclusive;
  return { correct };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x\\in ${intervalLatex(data.boundary, data.finalLess, data.inclusive)}`} />;
}

export function renderUserAnswer(userInput = {}) {
  const raw = typeof userInput?.S === "string" ? userInput.S : userInput?.S?.raw || "";
  return raw ? <InlineMath math={intervalToLatex(raw, { withX: true })} /> : <i>(brak odpowiedzi)</i>;
}

export function renderExplanation({ data }) {
  const {
    leftDenominator, rightDenominator, commonMultiple,
    leftMultiplier, rightMultiplier, leftCoefficient, rightCoefficient,
    leftConstant, rightConstant, reducedCoefficient, reducedConstant,
    boundary, kind, finalKind, finalLess, inclusive, latex,
  } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> <InlineMath math={`\\operatorname{NWW}(${leftDenominator},${rightDenominator})=${commonMultiple}`} />.
        Mnożymy obie strony przez dodatnią liczbę <InlineMath math={`${commonMultiple}`} />, więc znak jeszcze się nie zmienia.
      </p>
      <BlockMath math={latex} />
      <BlockMath math={`${leftMultiplier}(${linearLatex(leftCoefficient, leftConstant)}) ${SIGN_LATEX[kind]} ${rightMultiplier}(${linearLatex(rightCoefficient, rightConstant)})`} />
      <p><strong>Krok 2.</strong> Rozwijamy nawiasy i porządkujemy.</p>
      <BlockMath math={`${coeffX(reducedCoefficient)} ${SIGN_LATEX[kind]} ${reducedConstant}`} />
      <p>
        <strong>Krok 3.</strong> Dzielimy przez ujemną liczbę <InlineMath math={`${reducedCoefficient}`} />, dlatego <strong>odwracamy znak nierówności</strong>.
      </p>
      <BlockMath math={`x ${SIGN_LATEX[finalKind]} ${boundary}`} />
      <BlockMath math={`x\\in ${intervalLatex(boundary, finalLess, inclusive)}`} />
      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        <NumberLineInterval t={boundary} tLabelLatex={`${boundary}`} dir={finalLess ? "left" : "right"} closed={inclusive} showZero />
      </div>
    </div>
  );
}
